import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Gamepad2, 
  FolderOpen, 
  Monitor, 
  FileText, 
  Search, 
  Users, 
  BarChart3, 
  Settings,
  LogOut,
  Newspaper,
  Globe,
  Rss,
  Menu
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { SEO } from '@/components/SEO';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  const { signOut } = useAuth();

  const menuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/games', icon: Gamepad2, label: 'Games' },
    { path: '/admin/games-feed', icon: Rss, label: 'Games Feed' },
    { path: '/admin/categories', icon: FolderOpen, label: 'Categories' },
    { path: '/admin/blog', icon: Newspaper, label: 'Blog' },
    { path: '/admin/pages', icon: FileText, label: 'Pages' },
    { path: '/admin/menus', icon: Menu, label: 'Menus' },
    { path: '/admin/ads', icon: Monitor, label: 'Ads' },
    { path: '/admin/seo', icon: Search, label: 'SEO' },
    { path: '/admin/sitemaps', icon: Globe, label: 'Sitemaps' },
    { path: '/admin/users', icon: Users, label: 'Users' },
    { path: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <SEO pageType="admin" noindex={true} title="Admin Panel" />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex">
        {/* Sidebar */}
        <div className="w-64 bg-white/90 backdrop-blur-xl border-r border-gray-200/50 shadow-lg flex flex-col fixed left-0 top-0 h-full z-30">
          {/* Header */}
          <div className="p-6 border-b border-gray-200/50">
            <Link to="/admin/dashboard" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center transform group-hover:scale-110 transition-all duration-300 shadow-lg">
                <Gamepad2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">GameHub</h1>
                <p className="text-sm text-gray-500">Admin Panel</p>
              </div>
            </Link>
          </div>

          {/* Navigation Menu */}
          <nav className="px-4 py-6 space-y-2 flex-1 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-300 group relative overflow-hidden ${
                    active
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg transform scale-105'
                      : 'text-gray-600 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:text-blue-600 hover:shadow-md'
                  } font-medium`}
                >
                  <Icon className={`w-5 h-5 transition-transform duration-300 ${active ? 'scale-110' : 'group-hover:scale-110'}`} />
                  <span className="relative z-10">{item.label}</span>
                  {active && (
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 opacity-10 rounded-xl"></div>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Footer with Sign Out */}
          <div className="p-4 border-t border-gray-200/50">
            <Button
              onClick={signOut}
              variant="ghost"
              className="w-full justify-start text-gray-600 hover:text-red-600 hover:bg-red-50 transition-all duration-300 rounded-xl"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Sign Out
            </Button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 ml-64">
          {/* Top Header Bar */}
          <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200/50 px-6 py-4 sticky top-0 z-20">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {menuItems.find(item => item.path === location.pathname)?.label || 'Admin Panel'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Manage your gaming platform
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500">
                  {new Date().toLocaleDateString()}
                </span>
              </div>
            </div>
          </header>

          {/* Page Content */}
          <main className="p-6">
            <div className="max-w-full mx-auto">
              {children}
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default AdminLayout;
